import React, { useMemo,useContext } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Customized,
} from "recharts";
import themeContext from "../../context/theme/ThemeContext";
// const normalityColorMap = {
//   normal: {
//     avg: "#28a745",     // Green (positive)
//     minMax: "#a9dfbf",  // Light green
//   },
//   abnormal: {
//     avg: "#dc3545",     // Red (negative)
//     minMax: "#f5b7b1",  // Light red
//   },
//   total: {
//     avg: "#6c757d",     // Gray (neutral)
//     minMax: "#d6d8db",  // Light gray
//   },
//   null: {
//     avg: "#6c757d",
//     minMax: "#d6d8db",
//   },
// };

const normalityColorMap = {
  normal: {
    avg: "#1abc9c",     // Teal (positive)
    minMax: "#a3e4d7",  // Soft teal
  },
  abnormal: {
    avg: "#e74c3c",     // Strong coral red (negative)
    minMax: "#f1948a",  // Light coral
  },
  total: {
    avg: "#fd8d3c",  
    minMax: "#5dade2",
  },
  null: {
    avg: "#fd8d3c",
    minMax: "#5dade2",
  },
};



const CustomVerticalLineWithDots = ({ data, xAxisMap, yAxisMap, colorSet }) => {
  const xScale = xAxisMap[Object.keys(xAxisMap)[0]].scale;
  const yScale = yAxisMap[Object.keys(yAxisMap)[0]].scale;

  return (
    <>
      {data.map((entry, index) => {
        const x = xScale(entry.x);
        const yMin = yScale(entry.min);
        const yMax = yScale(entry.max);

        return (
          <g key={index}>
            {/* Vertical dashed line */}
            <line
              x1={x}
              x2={x}
              y1={yMin}
              y2={yMax}
              stroke={colorSet.avg}
              strokeWidth={1.5}
              strokeDasharray="5 5"
              opacity={0.6}
            />

            {/* Min dot */}
            <circle
              cx={x}
              cy={yMin}
              r={5}
              fill={colorSet.minMax}
              stroke="#fff"
              strokeWidth={1}
            />

            {/* Max dot */}
            <circle
              cx={x}
              cy={yMax}
              r={5}
              fill={colorSet.minMax}
              stroke="#fff"
              strokeWidth={1}
            />
          </g>
        );
      })}
    </>
  );
};

const CustomTooltip = ({ active, payload, label, colorSet, chartType }) => {
  if (!active || !payload || payload.length === 0) return null;

  // payload[0] is Average TAT line point
  const { payload: dataPoint } = payload[0];

  const formatTAT = (value) => {
    return chartType === "radiologist_tat_trend"
      ? formatSecondsToMMSS(value)
      : value;
  };

  return (
    <div
      style={{
        background: "white",
        padding: "8px 12px",
        borderRadius: "6px",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.15)",
        fontSize: "13px",
        lineHeight: "1.6",
        color: "#333",
      }}
    >
      <div>
        <strong>{formatDateDDMMMYY(label)}</strong>
      </div>
      {dataPoint.max != null && (
        <div style={{ color: colorSet.minMax }}>
         <strong>Max TAT: {formatTAT(dataPoint.max)}</strong>
        </div>
      )}
      {dataPoint.avg != null && (
        <div style={{ color: colorSet.avg }}>
          <strong>Avg TAT: {formatTAT(dataPoint.avg)}</strong>
        </div>
      )}
      {dataPoint.min != null && (
        <div style={{ color: colorSet.minMax }}>
          <strong>Min TAT: {formatTAT(dataPoint.min)}</strong>
        </div>
      )}

    </div>
  );
};

const formatSecondsToMMSS = (seconds) => {
  const mm = Math.floor(seconds / 60);
  const ss = Math.floor(seconds % 60);
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
};

const formatDateDDMMMYY = (value) => {
  const date = new Date(value);
  if (isNaN(date)) return value; // fallback if invalid date

  const day = String(date.getDate()).padStart(2, "0");
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", 
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);

  return `${day}-${month}-${year}`;
};


const CustomXAxisLabel = ({ viewBox }) => {
  const {getAlterColor} = useContext(themeContext);
  const { x, y, width } = viewBox;
  return (
    <text
      x={x + width / 2}
      y={y + 75}
      textAnchor="middle"
      fill={getAlterColor("#ffffff", "#000")}
      fontSize={12}
      fontWeight="bold"
    >
      <tspan x={x + width / 2} dy={0}>Submit Date</tspan>
      <tspan x={x + width / 2} dy={15} fontSize={10} fontWeight="normal">
        (DD-MMM-YYYY)
      </tspan>
    </text>
  );
};

const CustomYAxisLabel = ({ viewBox, chartType }) => {
  const {getAlterColor} = useContext(themeContext)
  const { x, y, height } = viewBox;
  const unit = chartType === "radiologist_tat_trend" ? "mm:ss" : "seconds";

  return (
    <text
      x={x - 70}
      y={(y + height / 2) + 50}
      textAnchor="middle"
      fill={getAlterColor("#ffffff", "#000")}
      fontSize={12}
      transform={`rotate(-90, ${x - 70}, ${y + height / 2})`}
    >
      <tspan fontWeight="bold">TAT</tspan>
      <tspan fontWeight="normal"> ({unit})</tspan>
    </text>
  );
};


const TATCombinedChart = ({
  data,
  normality, // Default to "null" if not provided
  yAxisText = "TAT (seconds)",
  xAxisText = "Submit Date (DD-MM-YYYY)",
  chartType,
  onPointDoubleClick,
}) => {
  const colorSet = useMemo(() => {
    return normalityColorMap[normality || "null"];
  }, [data]);
  const { getThemeColor,getAlterColor } = useContext(themeContext);
  const isPortraitMode = window.innerHeight > window.innerWidth && window.innerWidth >= 800;

const [paddedMin, paddedMax] = useMemo(() => {
    const allMins = data.map((d) => d.min).filter((v) => v != null);
    const allMaxs = data.map((d) => d.max).filter((v) => v != null);

    const globalMin = Math.min(...allMins);
    const globalMax = Math.max(...allMaxs);

    const range = globalMax - globalMin || 1; // prevent 0 range
    const padding = range * 0.1; // 10% padding

    const safeMin = Math.max(0, Math.floor(globalMin - padding));
    const safeMax = Math.ceil(globalMax + padding);

    return [safeMin, safeMax];
  }, [data]);



  const paddingSize = data.length === 2 ? 200 : data.length === 3 ? 120 : data.length === 4 ? 70 : data.length === 5 ? 60 : 40;

  const legendItems = [
    { name: "Max TAT", color: colorSet.minMax },
    { name: "Average TAT", color: colorSet.avg },
    { name: "Min TAT", color: colorSet.minMax },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
    <ResponsiveContainer width="100%" height="100%" style={{ marginTop: 20 }}>
      {/* <ComposedChart data={data} > */}
      <ComposedChart data={data} margin={{ top: 20, right: 10, bottom: isPortraitMode ? 70 : 70, left: 30 }}
        onClick={(e) => {
        if (onPointDoubleClick && e && e.activeLabel) {
          onPointDoubleClick(e.activeLabel);  // Pass clicked date label
        }
      }}
      >
        <CartesianGrid stroke="#555" strokeDasharray="3 3" />
        <XAxis
          dataKey="x"
          // label={{ value: xAxisText, position: "insideBottom", offset: -10, dy: 40 }}
          label={<CustomXAxisLabel />}
          stroke="#555"
          tick={{ fill: getAlterColor("#ffffff", "#000"), fontSize: 12, angle: -45, textAnchor: "end", dy: 4, dx: -4 }}
          padding={{ left: paddingSize, right: paddingSize }}
          minTickGap={20}
          interval={data.length > 6 ? 1 : 0}
          tickFormatter={formatDateDDMMMYY}
        />
        <YAxis
          domain={[paddedMin, paddedMax]}
          // domain={[
          //   (dataMin) => Math.floor(dataMin - 3), // Tight, but avoids hitting 0
          //   (dataMax) => Math.ceil(dataMax + 3),
          // ]}
          // label={{
          //   value: yAxisText,
          //   angle: -90,
          //   position: "insideLeft",
          //   style: { fill: "#ccc", fontSize: 12 },
          // }}
          label={<CustomYAxisLabel chartType={chartType} />}
          stroke="#555"
          tick={{ fill: getAlterColor("#ffffff", "#000"), fontSize: 12 }}
          tickCount={8}
          tickFormatter={chartType === "radiologist_tat_trend" ? formatSecondsToMMSS : undefined}
        />
        <Tooltip content={<CustomTooltip colorSet={colorSet} chartType={chartType} />} cursor={false} />
        {!isPortraitMode && (
          <Legend
            layout="vertical"
            verticalAlign="top"
            align="right"
            wrapperStyle={{ paddingLeft: 20, color: "#ccc", fontSize: 13 }}
            iconType="square"
          />
        )}
         <Line
          dataKey="max"
          name="Max TAT"
          stroke={colorSet.minMax}
          strokeWidth={0}
          dot={false}
          legendType="circle"
          hide={false}
        />

        <Line
          type="monotone"
          dataKey="avg"
          name="Average TAT"
          stroke={colorSet.avg}
          strokeWidth={3}
          dot={{ r: 6, fill: colorSet.avg, strokeWidth: 2 }}
          activeDot={{ r: 7 }}
        />

        <Line
          dataKey="min"
          name="Min TAT"
          stroke={colorSet.minMax}
          strokeWidth={0}
          dot={false}
          legendType="circle"
          hide={false}
        />
        <Customized component={<CustomVerticalLineWithDots data={data} colorSet={colorSet} />} />
      </ComposedChart>
    </ResponsiveContainer>
    {isPortraitMode && (
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "8px 16px", padding: "6px 8px", flexShrink: 0 }}>
        {legendItems.map((item) => (
          <div key={item.name} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "13px", color: "#ccc" }}>
            <div style={{ width: "12px", height: "12px", borderRadius: "2px", backgroundColor: item.color, flexShrink: 0 }} />
            <span>{item.name}</span>
          </div>
        ))}
      </div>
    )}
    </div>
  );
};

export default TATCombinedChart;
